<template>
  <login
    v-if="
      (this.$route.query.token && displayLogin) ||
      (!this.$route.query.token && !isB2BPlatform) ||
      (!this.$route.query.token && isB2BPlatform && content.active)
    "
    :action="action"
    @loginSuccess="handleLoginSuccess"
    :isB2B="content?.active"
    :b2b-content="content"
    :method="method"
    :singleSignOn="isSingleSignOn"
  >
    <template v-if="!content.active" #resetPassword>
      <ez-button
        v-if="!isSingleSignOn"
        type="secondary"
        isFullWidth
        @click="changeLogInMethod('magic_link')"
        >Sign in via email link</ez-button
      >
      <ez-button
        v-else-if="isSingleSignOn"
        type="secondary"
        isFullWidth
        @click="changeLogInMethod('password')"
        >Sign in with password</ez-button
      >
      <router-link :to="{ name: 'platform-forgot-password' }">Forgot Password?</router-link>
      <router-link :to="{ name: 'terms-and-conditions' }" target="_blank"
        >Terms and Conditions</router-link
      >
    </template>
    <template v-else #resetPassword>
      <div class="register-link">
        Don't Have an Account?
        <a :href="`/onboarding?token=${content.onboardingToken}`">Register Now</a>
      </div>
    </template>
  </login>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import flash from '@/components/ui/FlashMessage';
import httpService from '@/api/http';
import qs from 'qs';
import { resetState } from '@/store';
import { Login } from '@/views/common/auth';
import { getAccountOwnerIdForLoggedUser, isLocalStorageAccessSafeFlash } from '@/util/utils';
import productsPermissionFix from '@/util/productsPermissionFix';
import { isAdmin } from '@/util/constants';
import EzButton from '@/components/ui/Button/EzButton.vue';

export default {
  components: {
    Login,
    EzButton,
  },
  data() {
    return {
      content: {},
      displayLogin: false,
      isSingleSignOn: false,
    };
  },
  computed: {
    platformName() {
      return process.env.VUE_APP_PLATFORM_TITLE;
    },
    action() {
      if (this.content.active || this.isSingleSignOn) return `public/b2b-portal/login`;
      if (this.platformName === 'Foodrazor') return '/venue/oauth';
      return '/manager/oauth';
    },
    loginAction() {
      if (this.platformName === 'Foodrazor') return '/venue/oauth';
      return '/manager/oauth';
    },
    method() {
      if (this.content.active || this.isSingleSignOn) return `put`;
      return 'post';
    },
    isB2BPlatform() {
      return ![
        'localhost',
        'app-dev.orderez.co',
        'app-dev.foodrazor.co',
        'app-staging.orderez.co',
        'app-staging.foodrazor.co',
        'app.orderez.co',
        'app.foodrazor.com',
      ].includes(window.location.hostname);
    },
  },
  methods: {
    ...mapActions('defaultImages', ['fetchDefaultImages']),
    ...mapActions('entities/venues', ['fetchB2BLoginContent']),
    ...mapMutations('reports', ['UPDATE_FILTERS_TO_DOS']),
    initIntercom() {
      window.Intercom('boot', {
        app_id: process.env.VUE_APP_INTERCOM_APP_ID,
      });
    },
    handleLoginSuccess(loginResponse) {
      if (this.content.active || this.isSingleSignOn) {
        flash.success({
          title: 'Email sent!',
        });
      } else {
        const tokenData = loginResponse.data.data;

        this.setTokens(tokenData);

        this.initUser(tokenData.scope)
          .then(() => {
            this.fetchDefaultImages();
            if (productsPermissionFix() === 'venue-orders') {
              this.$router.push({ name: 'venue-orders' });
            } else if (productsPermissionFix() === 'venue-orders-history') {
              this.$router.push({ name: 'venue-orders-history' });
            } else {
              this.$router.push({ name: 'platform-home' });
            }
            if (!isAdmin()) {
              this.UPDATE_FILTERS_TO_DOS({ accountOwnerId: getAccountOwnerIdForLoggedUser() });
            }
          })
          .catch(error => {
            this.resetTokens();

            flash.error({
              title: error.message,
            });

            throw error;
          });
      }
    },
    async initUser(scope) {
      switch (scope) {
        case 'distributor':
          return this.initDistributorLoggedUser();
        case 'venue':
          return this.initVenueLoggedUser();
        default:
          return Promise.reject(new Error(`Invalid scope ${scope}`));
      }
    },
    setTokens(tokens) {
      const {
        accessToken,
        accessTokenExpiresAt,
        refreshToken,
        refreshTokenExpiresAt,
        scope,
        user,
      } = tokens;
      // Get permissions for the logged in user.
      const { permissions } = user;

      if (!isLocalStorageAccessSafeFlash()) {
        return;
      }

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('accessTokenExpiresAt', accessTokenExpiresAt);
      localStorage.setItem('accessTokenExpiresAt', accessTokenExpiresAt);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('refreshTokenExpiresAt', refreshTokenExpiresAt);
      localStorage.setItem('role', scope);
      localStorage.setItem('permissions', JSON.stringify(permissions));
    },
    resetTokens() {
      this.EMPTY_CART();

      if (!isLocalStorageAccessSafeFlash()) {
        return;
      }
      const logInMethod = localStorage.getItem('logInMethod');
      localStorage.clear();
      localStorage.setItem('logInMethod', logInMethod);
    },
    changeLogInMethod(value) {
      this.isSingleSignOn = value === 'magic_link';
      localStorage.setItem('logInMethod', value);
    },
    ...mapActions('entities/users', ['initVenueLoggedUser', 'initDistributorLoggedUser']),
    ...mapMutations('cart', ['EMPTY_CART']),
  },
  async created() {
    this.initIntercom();
    const logInMethod = localStorage.getItem('logInMethod');
    this.isSingleSignOn = logInMethod === 'magic_link';
    if (this.$route.query.token) {
      resetState();
      try {
        const { data: loginData } = await httpService.post(
          this.loginAction,
          qs.stringify({
            user_public_token: this.$route.query.token,
            grant_type: 'user_public_token',
            client_id: 'web',
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          },
        );
        this.setTokens(loginData.data);
        await this.initUser(loginData.data.scope);
        /* eslint no-underscore-dangle: 0 */
        this.$permission._pullPermissions();
        this.fetchDefaultImages();

        if (this.$route.query.redirectTo) {
          this.$router.push({
            name: this.$route.query.redirectTo,
            ...(this.$route.query.orderId ? { params: { id: this.$route.query.orderId } } : {}),
          });
        } else if (this.isB2BPlatform && this.$route.query?.page === 'shopping_cart') {
          this.$router.push({ name: 'venue-shopping-cart' });
        } else if (this.isB2BPlatform) {
          this.$router.push({ name: 'venue-orders-products' });
        } else if (productsPermissionFix() === 'venue-orders') {
          this.$router.push({ name: 'venue-orders' });
        } else if (productsPermissionFix() === 'venue-orders-history') {
          this.$router.push({ name: 'venue-orders-history' });
        } else {
          this.$router.push({ name: 'platform-home' });
        }
      } catch (e) {
        console.error(e);
        this.displayLogin = true;
      }
    }
    if (this.isB2BPlatform) {
      try {
        const { data } = await this.fetchB2BLoginContent();
        this.content = data.data;
      } catch (e) {
        console.error(e);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
:deep() .register-link {
  width: 100%;
  font-weight: 600;
  color: $color-gray-25;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  a {
    font-weight: 500;
    display: inline-block;
  }
}
</style>
